import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Spotlight.css";
import Projects from "../data/projects";
import {
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
  FaShip,
  FaTools,
  FaCheckCircle,
  FaPlay,
  FaPause,
  FaExternalLinkAlt,
  FaCircle,
} from "react-icons/fa";

const completedProjects = Projects.filter((p) => p.completed);

const SLIDE_DURATION = 7500; // 7.5 seconds per slide

const Spotlight = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  // Touch tracking for mobile swipe
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isHovered = useRef(false);

  const totalSlides = completedProjects.length;
  const project = React.useMemo(
    () => completedProjects[currentIndex] || {},
    [currentIndex]
  );

  // Gather all available images for the active project
  const allImages = React.useMemo(() => {
    const list = [];
    if (project.mainImages) {
      if (Array.isArray(project.mainImages)) {
        list.push(...project.mainImages);
      } else {
        list.push(project.mainImages);
      }
    }
    if (Array.isArray(project.additionalImages)) {
      project.additionalImages.forEach((img) => {
        if (img && !list.includes(img)) {
          list.push(img);
        }
      });
    }
    return list.filter(Boolean);
  }, [project]);

  // Reset active image when project slide changes
  useEffect(() => {
    setActiveImageIndex(0);
    setProgress(0);
  }, [currentIndex]);

  const nextSlide = useCallback(() => {
    if (totalSlides === 0) return;
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    if (totalSlides === 0) return;
    setCurrentIndex((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  }, [totalSlides]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Autoplay timer with progress bar
  useEffect(() => {
    if (!isPlaying || totalSlides <= 1) return;

    const intervalStep = 50; // update progress every 50ms
    const stepIncrement = (intervalStep / SLIDE_DURATION) * 100;

    const timer = setInterval(() => {
      if (isHovered.current) return;

      setProgress((prev) => {
        if (prev >= 100) {
          nextSlide();
          return 0;
        }
        return prev + stepIncrement;
      });
    }, intervalStep);

    return () => clearInterval(timer);
  }, [isPlaying, totalSlides, nextSlide]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        prevSlide();
      } else if (e.key === "ArrowRight") {
        nextSlide();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [prevSlide, nextSlide]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // min swipe distance in px
    if (diff > threshold) {
      nextSlide(); // Swiped left -> next
    } else if (diff < -threshold) {
      prevSlide(); // Swiped right -> prev
    }
  };

  // Helper to format title safely (handles array or string)
  const formatTitle = (title) => {
    if (!title) return "Marine Engineering Project";
    if (Array.isArray(title)) {
      return title[0];
    }
    return title;
  };

  // Helper to clean and format work completed items
  const getWorkItems = (description) => {
    if (!description) return [];
    const items = Array.isArray(description) ? description : [description];
    return items
      .map((item) =>
        item
          .replace(/^[T•\s➡-]+/, "")
          .replace(/[⚓✨]/g, "")
          .trim()
      )
      .filter((item) => item.length > 5 && !item.toLowerCase().includes("ready to deliver reliable") && !item.toLowerCase().includes("one click away"));
  };

  const activeImage =
    allImages[activeImageIndex] ||
    (Array.isArray(project.mainImages) ? project.mainImages[0] : project.mainImages);

  const workItems = getWorkItems(project.description);

  if (totalSlides === 0) return null;

  return (
    <section className="ciap-spotlight-section" id="spotlight">
      <div className="ciap-spotlight-inner">
        {/* SECTION HEADER */}
        <div className="ciap-spotlight-header">
          <div className="ciap-spotlight-badge">
            <span className="ciap-spotlight-badge-dot"></span>
            <span>Featured Showcase</span>
          </div>
          <h2 className="ciap-spotlight-title">In the Spotlight</h2>
          <p className="ciap-spotlight-subtitle">
            Highlighting our recent high-precision repairs, emergency interventions, and turnkey marine engineering solutions.
          </p>
        </div>

        {/* SHOWCASE CARD */}
        <div
          className="ciap-spotlight-card"
          onMouseEnter={() => {
            isHovered.current = true;
          }}
          onMouseLeave={() => {
            isHovered.current = false;
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* MEDIA COLUMN (LEFT) */}
          <div className="ciap-spotlight-media">
            <div className="ciap-spotlight-img-wrap">
              <img
                key={`${project.id}-${activeImageIndex}`}
                src={activeImage}
                alt={formatTitle(project.title)}
                className="ciap-spotlight-img"
                loading="lazy"
              />
            </div>

            {/* Gradient overlays for contrast */}
            <div className="ciap-spotlight-media-gradient"></div>

            {/* Top Bar Badges */}
            <div className="ciap-spotlight-media-top">
              <span className="ciap-spotlight-status-tag">
                <FaCheckCircle /> Completed Project
              </span>
              <span className="ciap-spotlight-counter">
                {String(currentIndex + 1).padStart(2, "0")} / {String(totalSlides).padStart(2, "0")}
              </span>
            </div>

            {/* Navigation Arrows */}
            <button
              className="ciap-spotlight-nav-btn prev"
              onClick={prevSlide}
              aria-label="Previous Project"
              type="button"
            >
              <FaChevronLeft />
            </button>
            <button
              className="ciap-spotlight-nav-btn next"
              onClick={nextSlide}
              aria-label="Next Project"
              type="button"
            >
              <FaChevronRight />
            </button>

            {/* Multi-image thumbnail strip (if project has multiple images) */}
            {allImages.length > 1 && (
              <div className="ciap-spotlight-thumbs" aria-label="Project images">
                {allImages.slice(0, 5).map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`ciap-spotlight-thumb-btn ${idx === activeImageIndex ? "active" : ""}`}
                    onClick={() => setActiveImageIndex(idx)}
                    aria-label={`View image ${idx + 1}`}
                  >
                    <img src={img} alt="" />
                  </button>
                ))}
              </div>
            )}

            {/* Progress bar */}
            {isPlaying && (
              <div className="ciap-spotlight-progress-wrap">
                <div
                  className="ciap-spotlight-progress-bar"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            )}
          </div>

          {/* CONTENT / DETAILS COLUMN (RIGHT) */}
          <div className="ciap-spotlight-content">
            <div>
              {/* Meta tags (Vessel, Location) */}
              <div className="ciap-spotlight-meta-tags">
                {project.vessel && (
                  <span className="ciap-spotlight-meta-pill vessel">
                    <FaShip /> {project.vessel.trim()}
                  </span>
                )}
                {project.location && (
                  <span className="ciap-spotlight-meta-pill location">
                    <FaMapMarkerAlt /> {project.location.trim()}
                  </span>
                )}
              </div>

              {/* Project Title */}
              <h3 className="ciap-spotlight-card-title">
                {formatTitle(project.title)}
              </h3>

              {/* Scope of Work */}
              {project.project && (
                <div className="ciap-spotlight-scope">
                  <div className="ciap-spotlight-scope-label">
                    <FaTools /> Scope Summary
                  </div>
                  <p className="ciap-spotlight-scope-text">{project.project}</p>
                </div>
              )}

              {/* Work Completed Checklist */}
              {workItems.length > 0 && (
                <div className="ciap-spotlight-work-section">
                  <div className="ciap-spotlight-work-header">
                    <FaCheckCircle style={{ color: "#00a8ff" }} /> Work Executed
                  </div>
                  <ul className="ciap-spotlight-work-list">
                    {workItems.slice(0, 4).map((line, idx) => (
                      <li key={idx} className="ciap-spotlight-work-item">
                        <FaCircle className="ciap-spotlight-work-icon" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Footer / Controls */}
            <div className="ciap-spotlight-footer">
              {project.link ? (
                <a
                  href={project.link.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ciap-spotlight-case-btn"
                >
                  <span>View Case Post</span>
                  <FaExternalLinkAlt size={12} />
                </a>
              ) : (
                <div />
              )}

              <div className="ciap-spotlight-playback-controls">
                {/* Play / Pause toggle */}
                <button
                  type="button"
                  className="ciap-spotlight-play-btn"
                  onClick={() => setIsPlaying((prev) => !prev)}
                  title={isPlaying ? "Pause Slideshow" : "Resume Slideshow"}
                  aria-label={isPlaying ? "Pause Slideshow" : "Resume Slideshow"}
                >
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </button>

                {/* Compact indicator dots for surrounding slides */}
                <div className="ciap-spotlight-indicators">
                  {completedProjects.slice(0, Math.min(6, totalSlides)).map((_, idx) => {
                    // Show a window of dots if totalSlides > 6
                    const currentWindowIndex = currentIndex % Math.min(6, totalSlides);
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`ciap-spotlight-dot ${idx === currentWindowIndex ? "active" : ""}`}
                        onClick={() => goToSlide(idx)}
                        aria-label={`Go to slide ${idx + 1}`}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Spotlight;